import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoHeader from '../../assets/logo_header.png';

const navLinks = [
  { label: 'About Us', path: '/about' },
  { label: 'Ethanol Ecosystem', path: '/ecosystem' },
  { label: 'Knowledge Hub', path: '/knowledge' },
  { label: 'OMC Tenders', path: '/tenders' },
  { label: 'BEA Represent', path: '/represent' },
  { label: 'Contact Us', path: '/contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${scrolled || menuOpen ? 'border-b border-gray-100 shadow-2xs' : ''}`}>
      {/* Subtle top credit bar */}
      <div className="bg-slate-50/90 border-b border-gray-100/80 px-4 md:px-8 py-0.5">
        <div className="max-w-7xl mx-auto flex justify-end">
          <p className="text-[9.5px] text-gray-400 font-normal tracking-wide">
            Developed and Maintained by{' '}
            <a
              href="https://avinyaresearch.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-bteal font-medium transition-colors underline decoration-gray-300 hover:decoration-bteal"
            >
              AVINYA RESEARCH LABS
            </a>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
          <img
            src={logoHeader}
            alt="BEA - Bihar Ethanol Association"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-2 text-[13px] font-medium rounded-md transition-all duration-150 whitespace-nowrap cursor-pointer ${isActive(link.path)
                ? 'text-bteal bg-bteal/8 font-semibold'
                : 'text-ink-muted hover:text-ink hover:bg-gray-50'
                }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/contact"
            className="hidden lg:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-bteal text-white text-[13px] font-semibold hover:bg-bteal-dark transition-all duration-150 whitespace-nowrap cursor-pointer"
          >
            Get In Touch
          </Link>
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md text-ink-muted hover:bg-gray-50 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-3">
          <nav className="flex flex-col gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${isActive(link.path)
                  ? 'bg-bteal/8 text-bteal font-semibold'
                  : 'text-ink-muted hover:bg-gray-50 hover:text-ink'
                  }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-2 px-4 py-2.5 rounded-md bg-bteal text-center text-white font-semibold text-sm cursor-pointer whitespace-nowrap block"
            >
              Get In Touch
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
